import React, { useState } from 'react';
import { Card, Button, Upload, message, Space, Divider, Progress, Alert, Modal } from 'antd';
import {
    DownloadOutlined,
    UploadOutlined,
    CloudDownloadOutlined,
    HistoryOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { useBackup } from '../../hooks/useBackup';
import styles from './ImportExport.module.css';
import {backupService} from '../../services/backupService'

const { Dragger } = Upload;

export default function ImportExport({ onDataImported }) {
    const { currentUser } = useAuth();
    const {
        exportData,
        importData,
        setupAutoBackup,
        getBackupHistory,
        downloadBackup
    } = useBackup();

    const [loading, setLoading] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    // Export dữ liệu
    const handleExport = async () => {
        setLoading(true);
        try {
            await exportData();
            message.success('Export dữ liệu thành công!');
        } catch (error) {
            message.error('Lỗi export: ' + error.message);
        }
        setLoading(false);
    };

    // Import dữ liệu
    const handleImport = async (file) => {
        setLoading(true);
        setShowProgress(true);
        setImportProgress(0);

        try {
            // Đọc file
            setImportProgress(20);
            const text = await file.text();
            const data = JSON.parse(text);

            // Validate data
            setImportProgress(40);
            if (!data.debts || !data.payments) {
                throw new Error('File không đúng định dạng! Thiếu dữ liệu nợ hoặc thanh toán.');
            }

            // Confirm import với thông tin chi tiết
            const confirmed = await new Promise((resolve) => {
                Modal.confirm({
                    title: 'Xác nhận import',
                    icon: <ExclamationCircleOutlined />,
                    content: (
                        <div>
                            <p>Bạn sắp import:</p>
                            <ul>
                                <li><strong>{data.debts.length}</strong> khoản nợ</li>
                                <li><strong>{data.payments.length}</strong> lần thanh toán</li>
                                {data.savings && (
                                    <li><strong>{data.savings.length}</strong> khoản tiết kiệm</li>
                                )}
                                {data.savingsTransactions && (
                                    <li><strong>{data.savingsTransactions.length}</strong> giao dịch tiết kiệm</li>
                                )}
                            </ul>
                            <Alert
                                message="Dữ liệu sẽ được thêm vào, không ghi đè dữ liệu hiện tại"
                                type="info"
                                style={{ marginTop: 12 }}
                            />
                            {data.version && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                                    Phiên bản file: {data.version}
                                </div>
                            )}
                        </div>
                    ),
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                    okText: 'Import',
                    cancelText: 'Hủy'
                });
            });

            if (!confirmed) {
                setShowProgress(false);
                setLoading(false);
                return false;
            }

            // Import data
            setImportProgress(60);
            await importData(data);

            setImportProgress(100);
            const importMessage = [
                `${data.debts.length} nợ`,
                `${data.payments.length} thanh toán`
            ];

            if (data.savings?.length) {
                importMessage.push(`${data.savings.length} tiết kiệm`);
            }

            if (data.savingsTransactions?.length) {
                importMessage.push(`${data.savingsTransactions.length} giao dịch tiết kiệm`);
            }

            message.success(`Import thành công: ${importMessage.join(', ')}!`);
            onDataImported?.();

            setTimeout(() => {
                setShowProgress(false);
            }, 1000);

        } catch (error) {
            message.error('Lỗi import: ' + error.message);
            setShowProgress(false);
        }

        setLoading(false);
        return false; // Prevent upload
    };


    // Thiết lập auto backup
    const handleSetupAutoBackup = () => {
        try {
            setupAutoBackup();
            message.success('Đã thiết lập backup tự động hàng ngày!');
        } catch (error) {
            message.error('Lỗi thiết lập backup: ' + error.message);
        }
    };

    // Export template
    const handleExportTemplate = () => {
        const template = backupService.generateTemplate();

        const jsonString = JSON.stringify(template, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'debt-savings-manager-template.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.info('Đã tải template mẫu bao gồm cả tiết kiệm!');
    };

    const backupHistory = getBackupHistory();

    return (
        <div className={styles.importExportContainer}>
            {/* Progress Modal */}
            <Modal
                title="Đang import dữ liệu..."
                open={showProgress}
                footer={null}
                closable={false}
                centered
            >
                <Progress
                    percent={importProgress}
                    status={importProgress === 100 ? 'success' : 'active'}
                    strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                    }}
                />
                <p style={{ textAlign: 'center', marginTop: 16 }}>
                    {importProgress < 40 && 'Đang đọc file...'}
                    {importProgress >= 40 && importProgress < 60 && 'Đang xác thực dữ liệu...'}
                    {importProgress >= 60 && importProgress < 100 && 'Đang import dữ liệu...'}
                    {importProgress === 100 && 'Hoàn thành!'}
                </p>
            </Modal>

            <Space direction="vertical" style={{ width: '100%' }} size="large">

                {/* Export Section */}
                <Card
                    title={
                        <div className={styles.sectionTitle}>
                            <DownloadOutlined />
                            <span>Export dữ liệu</span>
                        </div>
                    }
                    className={styles.sectionCard}
                >
                    <div className={styles.cardContent}>
                        <p>Xuất tất cả dữ liệu nợ và thanh toán ra file JSON để backup hoặc chuyển đổi.</p>
                        <Space>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleExport}
                                loading={loading}
                                size="large"
                            >
                                Export dữ liệu hiện tại
                            </Button>
                            <Button
                                icon={<CloudDownloadOutlined />}
                                onClick={handleExportTemplate}
                                size="large"
                            >
                                Tải template mẫu
                            </Button>
                        </Space>
                    </div>
                </Card>

                <Divider />

                {/* Import Section */}
                <Card
                    title={
                        <div className={styles.sectionTitle}>
                            <UploadOutlined />
                            <span>Import dữ liệu</span>
                        </div>
                    }
                    className={styles.sectionCard}
                >
                    <div className={styles.cardContent}>
                        <Alert
                            message="Lưu ý quan trọng"
                            description="Dữ liệu import sẽ được thêm vào hệ thống, không ghi đè dữ liệu hiện có. Hãy đảm bảo file JSON đúng định dạng."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Dragger
                            accept=".json"
                            beforeUpload={handleImport}
                            showUploadList={false}
                            disabled={loading}
                            className={styles.uploadArea}
                        >
                            <p className="ant-upload-drag-icon">
                                <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                            </p>
                            <p className="ant-upload-text">
                                Kéo thả file JSON vào đây hoặc click để chọn file
                            </p>
                            <p className="ant-upload-hint">
                                Chỉ hỗ trợ file JSON được export từ hệ thống này hoặc theo template
                            </p>
                        </Dragger>
                    </div>
                </Card>

                <Divider />

                {/* Auto Backup Section */}
                <Card
                    title={
                        <div className={styles.sectionTitle}>
                            <HistoryOutlined />
                            <span>Backup tự động</span>
                        </div>
                    }
                    className={styles.sectionCard}
                >
                    <div className={styles.cardContent}>
                        <p>Thiết lập backup tự động để bảo vệ dữ liệu của bạn.</p>

                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div className={styles.backupStatus}>
                                <div className={styles.statusItem}>
                                    <span>Trạng thái: </span>
                                    <span className={styles.statusActive}>
                                        <CheckCircleOutlined /> Đã kích hoạt
                                    </span>
                                </div>
                                <div className={styles.statusItem}>
                                    <span>Backup cuối: </span>
                                    <span>{backupHistory.lastBackup || 'Chưa có'}</span>
                                </div>
                            </div>

                            <Space>
                                <Button
                                    type="dashed"
                                    icon={<HistoryOutlined />}
                                    onClick={handleSetupAutoBackup}
                                    size="large"
                                >
                                    Kích hoạt backup tự động
                                </Button>

                                <Button
                                    size="large"
                                    onClick={() => downloadBackup()}
                                    disabled={!backupHistory.hasBackup}
                                >
                                    Tải backup gần nhất
                                </Button>
                            </Space>
                        </Space>
                    </div>
                </Card>

                {/* Backup History */}
                <Card
                    title={
                        <div className={styles.sectionTitle}>
                            <HistoryOutlined />
                            <span>Lịch sử backup</span>
                        </div>
                    }
                    className={styles.sectionCard}
                >
                    <div className={styles.cardContent}>
                        {backupHistory.history.length > 0 ? (
                            <div className={styles.historyList}>
                                {backupHistory.history.map((backup, index) => (
                                    <div key={index} className={styles.historyItem}>
                                        <div className={styles.historyInfo}>
                                            <div className={styles.historyDate}>{backup.date}</div>
                                            <div className={styles.historyDetails}>
                                                <span className={styles.historySize}>{backup.size}</span>
                                                <div className={styles.historyStats}>
                                                    {backup.debtsCount} nợ, {backup.paymentsCount} thanh toán
                                                    {backup.savingsCount > 0 && (
                                                        <>, {backup.savingsCount} tiết kiệm</>
                                                    )}
                                                </div>
                                                {backup.version && (
                                                    <div className={styles.historyVersion}>v{backup.version}</div>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            size="small"
                                            onClick={() => downloadBackup(backup.id)}
                                        >
                                            Tải xuống
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.noHistory}>
                                <p>Chưa có lịch sử backup nào</p>
                            </div>
                        )}
                    </div>
                </Card>

            </Space>
        </div>
    );
}
